import { apiClient } from '../auth-lib';

// ─────────────────────────────────────────────────────────────────────────────
// The notifications this firm sends that NOBODY WRITES.
//
// "Send Notifications" is the one screen where a person types the words. Every
// other notification in the app is fired by a job on a schedule — and the two
// get confused constantly. Somebody who wants the DSC reminder moved to 08:00
// opens the composer, finds no clock anywhere on it, and concludes the reminder
// is not something the firm can change. This list is the answer to "where is
// that set, then", and it sits on the composer because that is where they went
// looking.
//
// ⚠️ ONLY THE ONES A FIRM CAN ACTUALLY CHANGE BELONG HERE. Plenty of jobs
// notify — jobs/noticeDeadline.js escalates at 14/7/3/1/0 days,
// jobs/approvalDigest.js reports the queue depth, jobs/expirableReminder.js
// chases renewals — but their timing is hard-coded, so an entry for one would
// be a row whose link lands on a screen with nothing to change. An entry here
// is a promise that `to` opens the settings themselves.
//
// ⚠️ `navKey` IS NOT DECORATION. The composer is gated `nav.users` and the DSC
// register on `nav.dsc` — two different keys, and a firm can hand out the first
// without the second. Listing an entry the reader cannot open hands them a link
// to a page that refuses them, which reads as a broken app rather than as a
// permission they were never given.
//
// Adding one (attendance is next): add the entry. The section on the composer
// renders whatever is in this array, and nothing else needs touching.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One line of state per entry: the pill beside the name and the sentence under
 * it. Shaped here rather than in the component so each entry can read its own
 * settings endpoint without the section knowing any of their shapes.
 *
 * @typedef  {object} NotificationState
 * @property {'success'|'warning'|'neutral'} variant  the StatusPill variant
 * @property {string} label   the pill's word — 'On', 'Off', …
 * @property {string} detail  the sentence under the description
 */

/** @returns {NotificationState} */
const describeDscDigest = (s) => {
  if (!s?.enabled) {
    return {
      variant: 'neutral',
      label: 'Off',
      detail: 'Nobody is told when a certificate lapses.',
    };
  }

  const when = `Daily at ${s.time}, covering the next ${s.leadDays} ${s.leadDays === 1 ? 'day' : 'days'}`;
  const who = [s.ownerName, s.reviewerName].filter(Boolean).join(' and ');

  // ⚠️ ON WITH NOBODY NAMED SENDS NOTHING. The digest goes to the preparer and
  // the reviewer and to no one else, so an empty pair makes
  // jobs/dscExpiryDigest.js return 'no-recipients' and move on — no error, no
  // mail, nothing in any bell. It is the one state worth flagging from a screen
  // away, because on the settings screen itself it looks switched on.
  if (!who) {
    return {
      variant: 'warning',
      label: 'Reaches nobody',
      detail: `${when} — but no preparer or reviewer is named, so it is sent to nobody.`,
    };
  }

  return { variant: 'success', label: 'On', detail: `${when} · ${who}` };
};

/**
 * The two ends of the working day, as one line.
 *
 * ⚠️ REPORTED AS ONE ENTRY, NOT TWO, because it is one arrangement: the
 * morning nudge and the evening check are the same conversation about the same
 * day, they are set on the same screen, and splitting them would put two rows
 * in this list that both link to the same place.
 *
 * @returns {NotificationState}
 */
const describeAttendanceDay = (s) => {
  const morning = s?.checkInReminderEnabled !== false;
  const evening = s?.autoClockOutEnabled !== false;
  // The in-app dialog. Not a notification in itself, but it is the thing that
  // collects the answer the notifications are asking for, so a firm reading
  // this list needs to know whether it is on.
  const prompt = s?.dayPromptEnabled !== false;

  if (!morning && !evening) {
    return {
      variant: 'neutral',
      label: 'Off',
      detail: 'Nobody is reminded to clock in, and no day is closed for them.',
    };
  }

  const lead = s?.checkInReminderMinutes ?? 15;
  const parts = [];
  if (morning) parts.push(`Clock-in reminder ${lead} min before ${s.shiftStartTime}`);
  if (evening) parts.push(`day closed at ${s.autoClockOutTime}`);
  const when = parts.join(' · ');

  // ⚠️ HALF-ON IS ITS OWN STATE, not a quiet "On". A firm that reminds people
  // at 09:45 and never closes the day ends up with open sessions running into
  // the following week, and the settings screen shows two independent
  // checkboxes with no hint that one without the other is a trap.
  if (!morning || !evening) {
    return {
      variant: 'warning',
      label: morning ? 'No day end' : 'No reminder',
      detail: morning
        ? `${when} — but nobody is clocked out, so a day left open runs on.`
        : `${when} — but nobody is reminded to clock in.`,
    };
  }

  return {
    variant: 'success',
    label: 'On',
    detail: prompt ? `${when} · asked in the app too` : `${when} · in the bell only`,
  };
};

export const AUTOMATIC_NOTIFICATIONS = [
  {
    key: 'dsc',
    name: 'DSC expiry digest',
    what: 'A daily list of the certificates that have lapsed or are about to, to the two people who answer for renewing them.',
    // ⚠️ `?settings=notifications` IS LOAD-BEARING — OrgAdminDSC opens the
    // schedule dialog when it sees it. Linking to the bare register instead
    // would land somebody on a table of every DSC in the firm with the thing
    // they came for still behind a button in the toolbar.
    to: '/org-admin/dsc?settings=notifications',
    navKey: 'nav.dsc',
    // Reading the schedule is open to the whole org (see dscRoutes.js: knowing
    // the register is watched, and by whom, is not sensitive), so this never
    // 403s for anybody the `navKey` above already let through.
    load: () => apiClient.get('/api/dscs/settings').then((r) => describeDscDigest(r.data)),
  },
  {
    key: 'attendance',
    name: 'Clock-in and day-end',
    what: 'A morning reminder to whoever has not clocked in, and an evening check for whoever is still clocked in when the day ends.',
    // The settings screen addresses its sections by slug, so this opens the
    // Attendance card directly rather than the index of all of them.
    to: '/org-admin/settings/attendance',
    // ⚠️ `nav.settings`, NOT `nav.attendance`. The attendance BOARD and the
    // attendance SETTINGS are two different screens behind two different keys,
    // and this row links to the second. Using the board's key would show the
    // row to a manager who can read the register and then refuse them at the
    // door.
    navKey: 'nav.settings',
    // Reading these is open to the whole org — the employee page shows the
    // standard day and the holiday list from the same call — so this never
    // 403s for anybody the key above already let through.
    load: () => apiClient.get('/api/attendance/settings')
      .then((r) => describeAttendanceDay(r.data?.settings)),
  },
];

// Exported for the tests, which pin the three states this can be in — the
// 'Reaches nobody' one especially, since nothing else in the app says it.
export { describeDscDigest, describeAttendanceDay };
